import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
} from "@tanstack/react-table";
import { TableColumnsTeamTransactions } from "./components/TableColumnsTeamTransactions";
import { useTransactions } from "../../../../app/hooks/useTransactions";
import { Transaction } from "@/app/entities/Transaction";
import { useAtom } from "jotai";
import { selectedIdfromTableRowTransaction } from "@/app/store";
import { useQuery } from "@tanstack/react-query";
import { configService } from "@/app/services/configService";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";

export const useTeamsTransactionsPageController = () => {
  const { id } = useParams();
  const [selectedItem, setSelectedItem] = useState<Transaction | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data: productsCatalog } = useQuery<ProductCatalogItem[]>({
    queryKey: ["productsCatalog"],
    queryFn: configService.getProductsCatalog,
  });

  const { columns } = TableColumnsTeamTransactions(productsCatalog);

  const { data, isFetching, isLoading, setParams } = useTransactions({});

  const transactions = data?.transactions;
  const [, setSelectedId] = useAtom(selectedIdfromTableRowTransaction);

  const table = useReactTable({
    data: transactions || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (item: Transaction) => {
    setSelectedItem(item);
    setSelectedId(item._id);
  };

  const handleSheetState = () => {
    setSheetOpen(!sheetOpen);
  };

  const setSkip = (value: number) => {
    setParams((state) => ({ ...state, skip: value }));
  };

  const setTeamId = useCallback(() => {
    if (id) {
      setParams((state) => ({ ...state, teamIds: id }));
    }
  }, [id, setParams]);

  useEffect(() => {
    setTeamId();
  }, [setTeamId]);

  const getPageIndex = () => {
    if (!data) return 0;
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1;
  };

  const getPageCount = () => {
    if (!data) return 0;
    return Math.ceil(data.pagination.total / data.pagination.limit);
  };

  const handleNextPage = () => {
    if (!data) return;
    const newPage = data.pagination.skip / data.pagination.limit + 1;
    if (getPageCount() === getPageIndex()) return;
    const skipValue = newPage * data.pagination.limit;
    setSkip(skipValue);
  };

  const handlePreviousPage = () => {
    if (!data) return;
    const newPage = data.pagination.skip / data.pagination.limit - 1;
    if (newPage < 0) return;
    const skipValue = newPage * data.pagination.limit;
    setSkip(skipValue);
  };

  return {
    table,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    isFetching,
    isLoading,
    handleRowClick,
    sheetOpen,
    setSheetOpen,
    handleSheetState,
    selectedItem,
  };
};
